import type { FC } from 'react'
import { CircularProgress } from '@/components/commons/circular-progress'
import { CheckCircleIcon, Lock03Icon, XCircleIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip } from '@/components/ui/tooltip'
import { objectKeys } from '@/helpers/utils'
import { Trans } from '@/translations/i18n'
import { ShellPartialFormSchema } from '../form-schemas'
import { useChangeStep } from '../hooks/use-change-step'
import { useExperienceShellFormContext } from '../hooks/use-experience-shell-form-context'
import { ShellStepKeyName } from '../types'

// Helper to check if a value is empty
const isEmpty = (value: unknown): boolean => {
	if (value === undefined || value === null || value === '') {
		return true
	}
	if (Array.isArray(value)) {
		// Check if array is empty or if all its objects have empty values
		return value.length === 0 || value.every((item) => isEmpty(item))
	}
	if (typeof value === 'object') {
		// For objects, check if all values are empty
		return Object.values(value).every((v) => isEmpty(v))
	}
	// For numbers or other primitives, they are not empty
	if (typeof value === 'number') {
		return false
	}
	return false
}

// Helper to check if all values in an object are empty
const isSomeEmpty = (obj: unknown): boolean => {
	if (!obj || typeof obj !== 'object') {
		return true
	}

	// For objects, check each value
	const values = Object.entries(obj)
	return values.some(([key, value]) => {
		// Skip checking specific fields that should not be considered for emptiness
		if (key === 'expertise_years' || key === 'residential_country_region') {
			return false
		}
		return isEmpty(value)
	})
}

export const Progress: FC<{
	experienceId: number
	isItineraryLocked: boolean
	isLegalLocked: boolean
	step: ShellStepKeyName
	galleryImagesCount: number
	isUpgrade: boolean
}> = ({
	experienceId,
	isItineraryLocked,
	isLegalLocked,
	step,
	galleryImagesCount,
	isUpgrade
}) => {
	const { watch } = useExperienceShellFormContext(isUpgrade)
	const changeStep = useChangeStep({
		experienceId,
		currentStep: step,
		isUpgrade
	})

	if (step === ShellStepKeyName.enum.photos) {
		return galleryImagesCount >= 1 ? (
			<CheckCircleIcon className='fill-success-500' size={18} />
		) : (
			<CircularProgress progress={0} size={18} />
		)
	}

	if (isItineraryLocked) {
		return (
			<Tooltip
				content={
					<p>
						<Trans
							components={{
								button: (
									<button
										className='inline-flex cursor-pointer items-center gap-1 underline disabled:cursor-not-allowed'
										disabled={
											changeStep.editExperienceMutation
												.isPending
										}
										onClick={() =>
											changeStep.handleChangeStep({
												stepToGoTo:
													ShellStepKeyName.enum
														.merchandising,
												isBack: true
											})
										}
										type='button'
									/>
								),
								spinner: changeStep.isBackLoading ? (
									<Spinner size={14} />
								) : (
									<div />
								)
							}}
							i18nKey='experience_shell:itinerary_lock_text'
						/>
					</p>
				}
				contentProps={{ align: 'start' }}
			>
				<Lock03Icon />
			</Tooltip>
		)
	}
	if (isLegalLocked) {
		return (
			<Tooltip
				content={
					<p>
						<Trans
							components={{
								button: (
									<button
										className='inline-flex cursor-pointer items-center gap-1 underline disabled:cursor-not-allowed'
										disabled={
											changeStep.editExperienceMutation
												.isPending
										}
										onClick={() =>
											changeStep.handleChangeStep({
												stepToGoTo:
													ShellStepKeyName.enum
														.experience,
												isBack: true
											})
										}
										type='button'
									/>
								),
								spinner: changeStep.isBackLoading ? (
									<Spinner size={14} />
								) : (
									<div />
								)
							}}
							i18nKey='experience_shell:legal_lock_text'
						/>
					</p>
				}
				contentProps={{ align: 'start' }}
			>
				<Lock03Icon />
			</Tooltip>
		)
	}

	const stepValues = watch(step)
	const parsedValues =
		ShellPartialFormSchema.shape[step].safeParse(stepValues)
	const errorsCount = parsedValues.success
		? 0
		: objectKeys(parsedValues.error.format()).length

	if (errorsCount > 0) {
		return <XCircleIcon className='fill-danger-500' size={18} />
	}

	if (isSomeEmpty(stepValues)) {
		return <CircularProgress progress={0} size={18} />
	}

	return <CheckCircleIcon className='fill-success-500' size={18} />
}
